/**
 * Builds a child's full name from the employee's own.
 *
 * An Arabic name is a lineage: given name, father, grandfather, family. A
 * child of this employee therefore repeats the employee's first two names and
 * the family name — asking a parent to type all four parts is asking them to
 * copy three of them from their own record.
 *
 * It does not hold when the father is someone else, which happens with
 * half-siblings sharing a mother. That case enters the name in full; see
 * `DependentFormValues.differentFather`.
 */

/** How many parts of the employee's name are needed to derive a child's. */
const MINIMUM_PARENT_PARTS = 3;

export interface ComposedChildName {
  readonly fullName: string;
  /** The parts taken from the employee, so the screen can show its working. */
  readonly inheritedAr: string;
}

/**
 * Returns null when the employee's own recorded name is too short to derive
 * from — better to ask than to invent a lineage for someone.
 */
export function composeChildName(
  childFirstName: string,
  employeeFullName: string,
): ComposedChildName | null {
  const first = childFirstName.trim();
  if (first === '') return null;

  // Split on any run of whitespace: the roster contains double spaces.
  const parts = employeeFullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length < MINIMUM_PARENT_PARTS) return null;

  const father = parts[0];
  const grandfather = parts[1];
  // The last recorded part is the family name, however many ancestors precede
  // it — the roster is not consistent about how many are written down.
  const family = parts[parts.length - 1];

  if (!father || !grandfather || !family) return null;

  const inherited = [father, grandfather, family].join(' ');

  return {
    fullName: `${first} ${inherited}`,
    inheritedAr: inherited,
  };
}
